import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { CopyField, Empty, Note, PageHead, Panel, RailCard, DeskSupport } from "@/components/portal/crm/kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Copy, Download, ImageIcon, Loader2, Lock, ShieldCheck } from "lucide-react";

export default function MediaKit() {
  const { isAdmin } = useOutletContext();
  const [kit, setKit] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/refer/media-kit").then((r) => setKit(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed) {
    return (
      <Panel testId="referrer-kit-error">
        <Empty>
          {isAdmin ? "The media kit belongs to an introducer account — open it from a partner login." : "We couldn't build your kit just now. Tell the desk and we'll fix it."}
        </Empty>
      </Panel>
    );
  }

  if (!kit) {
    return <div className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-slate-300" /></div>;
  }

  return (
    <>
      <PageHead
        eyebrow="Everything carries your tracking code"
        title="Media kit"
        lede={`Artwork and wording built around ${kit.company}'s own link. Anyone who applies through any of it is credited to you automatically — nothing here needs editing.`}
        testId="referrer-kit-head"
      />

      {kit.locked && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[12px] border border-[#EFC35B] bg-[#FFEFC4] px-4 py-3.5 text-[13px] text-[#7A4800]" data-testid="referrer-kit-locked">
          <Lock className="size-4 shrink-0" />
          <span><b>Sign your agreement first.</b> You can look at everything here, but please don't publish any of it until your terms are signed.</span>
        </div>
      )}

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <Panel title="Your link" sub="Every asset below points here. It is the only link that credits a case to you." testId="referrer-kit-link">
            <CopyField value={kit.apply_url} label="Tracked application link" testId="referrer-kit-url" />
          </Panel>

          <Panel title="Artwork" sub="Rendered with your company name on it, at the size the platform actually wants." pad={false} testId="referrer-kit-assets">
            <div className="grid gap-px bg-[#E2E8F0] sm:grid-cols-2">
              {kit.assets.map((a, i) => <AssetCard key={a.key} asset={a} order={i} />)}
            </div>
          </Panel>

          <Panel title="Wording you can use" sub="Written to be sent as it is. Replace the square brackets and nothing else." pad={false} testId="referrer-kit-wording">
            <div className="divide-y divide-[#E2E8F0]">
              {kit.wording.map((w) => <WordingBlock key={w.key} block={w} />)}
            </div>
          </Panel>

          {kit.ads && <AdPack ads={kit.ads} />}
        </div>

        <div className="space-y-4">
          <RailCard title="The rules" testId="referrer-kit-rules">
            <ul className="space-y-3">
              {kit.rules.map((r) => (
                <li key={r} className="flex gap-3 text-[12.5px] leading-relaxed text-slate-600">
                  <ShieldCheck className="mt-[3px] size-3.5 shrink-0 text-[#0B6B4F]" />{r}
                </li>
              ))}
            </ul>
          </RailCard>

          <DeskSupport body="Need something in a size that isn't here, or your own logo alongside ours? Ask and we'll make it." />
          <Note>Anything you publish is a financial promotion. Keep the risk line on it and never quote a rate.</Note>
        </div>
      </div>
    </>
  );
}

const queue = { at: 0 };

/** The paid-media pack — copy that can run today, and the claims that get an ad account banned. */
function AdPack({ ads }) {
  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
    catch { toast.error("Copy failed"); }
  };
  const Group = ({ label, items, hint }) => (
    <div className="px-5 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <button onClick={() => copy(items.join("\n\n"), label)} className="text-[11.5px] font-semibold text-[#0055FF]"
                data-testid={`ad-copy-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>Copy all</button>
      </div>
      {hint && <p className="mt-1 text-[11.5px] text-slate-400">{hint}</p>}
      <div className="mt-2.5 space-y-2">
        {items.map((t) => (
          <button key={t} onClick={() => copy(t, "Copy")}
                  className="block w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-left text-[12.5px] leading-relaxed text-slate-700 hover:border-slate-300">
            {t}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Panel title="Ads & compliance" pad={false} testId="referrer-kit-ads"
           sub="Everything here has been through our desk. Run it as it is and your account stays safe; strengthen a claim and it becomes yours to defend.">
      <div className="divide-y divide-[#E2E8F0]">
        <Group label="Primary text" items={ads.primary_text} hint="Meta, TikTok and LinkedIn body copy" />
        <Group label="Headlines" items={ads.headlines} />
        <Group label="Descriptions" items={ads.descriptions} />
        <Group label="Google RSA headlines" items={ads.google_rsa.headlines} hint="30 characters or under" />
        <Group label="Google RSA descriptions" items={ads.google_rsa.descriptions} hint="90 characters or under" />

        <div className="px-5 py-4">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">Always</div>
          <ul className="mt-2.5 space-y-2">
            {ads.must.map((m) => (
              <li key={m} className="flex gap-2.5 text-[12.5px] leading-relaxed text-slate-600">
                <Check className="mt-[3px] size-3.5 shrink-0 text-[#0B6B4F]" />{m}
              </li>
            ))}
          </ul>
        </div>

        <div className="px-5 py-4">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#B42318]">Never</div>
          <ul className="mt-2.5 space-y-2" data-testid="referrer-kit-ads-never">
            {ads.never.map((m) => (
              <li key={m} className="flex gap-2.5 text-[12.5px] leading-relaxed text-slate-600">
                <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-[#B42318]" />{m}
              </li>
            ))}
          </ul>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">The disclaimer</div>
            <button onClick={() => copy(ads.disclaimer, "Disclaimer")} className="text-[11.5px] font-semibold text-[#0055FF]"
                    data-testid="ad-copy-disclaimer">Copy</button>
          </div>
          <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-[12.5px] leading-relaxed text-slate-700">
            {ads.disclaimer}
          </p>
        </div>

        <div className="px-5 py-4">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">Your own landing pages</div>
          <ul className="mt-2.5 space-y-2">
            {ads.landing.map((m) => (
              <li key={m} className="flex gap-2.5 text-[12.5px] leading-relaxed text-slate-600">
                <ShieldCheck className="mt-[3px] size-3.5 shrink-0 text-[#0B6B4F]" />{m}
              </li>
            ))}
          </ul>
          <div className="mt-3.5"><Note testId="referrer-kit-ads-check">{ads.checked_by_desk}</Note></div>
        </div>
      </div>
    </Panel>
  );
}

function AssetCard({ asset, order }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const blobRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(asset.url.replace("/api", ""), { responseType: "blob" });
        if (!alive) return;
        blobRef.current = r.data;
        setSrc(URL.createObjectURL(r.data));
      } catch { /* a missing preview is not an error worth shouting about */ }
    }, 120 * order);
    return () => { alive = false; clearTimeout(t); if (src) URL.revokeObjectURL(src); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.url, order]);

  const download = async () => {
    setBusy(true);
    try {
      const blob = blobRef.current || (await api.get(asset.url.replace("/api", ""), { responseType: "blob" })).data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = asset.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${asset.label} downloaded`);
    } catch { toast.error("That artwork could not be generated — tell the desk"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white p-5" data-testid={`referrer-kit-asset-${asset.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-archivo text-[14.5px] font-bold tracking-[-0.015em] text-[#0B1220]">{asset.label}</div>
          <div className="mt-1 text-[12.5px] text-slate-500">{asset.use}</div>
        </div>
        <span className="shrink-0 rounded-md bg-[#F4F6F9] px-2 py-1 font-archivo text-[11px] font-bold tabular-nums text-slate-500">{asset.px}</span>
      </div>

      <div className="mt-3.5 grid min-h-[132px] place-items-center overflow-hidden rounded-[11px] border border-[#E2E8F0] bg-[#F8FAFF] p-2.5">
        {src ? (
          <img src={src} alt={asset.label} className="max-h-[220px] w-full object-contain" data-testid={`referrer-kit-preview-${asset.key}`} />
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-300">
            <ImageIcon className="size-5" />
            <span className="text-[11.5px] font-semibold">rendering…</span>
          </div>
        )}
      </div>

      <Button onClick={download} disabled={busy} variant="outline"
              className="mt-3.5 h-10 w-full rounded-[10px] text-[12.5px] font-bold" data-testid={`referrer-kit-download-${asset.key}`}>
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <><Download className="mr-1.5 size-3.5" /> Download PNG</>}
      </Button>
    </div>
  );
}

function WordingBlock({ block }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block.body);
      setDone(true); toast.success("Copied");
      setTimeout(() => setDone(false), 1600);
    } catch { toast.error("Copy failed — select the text instead"); }
  };
  return (
    <div className="p-5" data-testid={`referrer-kit-wording-${block.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-archivo text-[14.5px] font-bold tracking-[-0.015em] text-[#0B1220]">{block.label}</div>
          <div className="mt-1 text-[12.5px] text-slate-500">{block.use}</div>
        </div>
        <Button onClick={copy} variant="outline" className="h-9 shrink-0 rounded-[10px] px-3 text-[12.5px] font-bold" data-testid={`referrer-kit-copy-${block.key}`}>
          {done ? <Check className="size-3.5 text-[#0B6B4F]" /> : <><Copy className="mr-1.5 size-3.5" /> Copy</>}
        </Button>
      </div>
      <pre className="mt-3.5 max-h-[280px] overflow-y-auto whitespace-pre-wrap rounded-[11px] border border-[#E2E8F0] bg-[#F8FAFF] p-4 font-sans text-[12.5px] leading-relaxed text-slate-700">
        {block.body}
      </pre>
    </div>
  );
}
